import isPropValid from '@emotion/is-prop-valid';

const excludeDefaultFilteringFlag = Symbol('excludeDefaultshouldForwardPropFilteringFlag');

export type StyledComponentType = 'intrinsic' | 'custom';
export type ShouldForwardProp = (prop: string) => boolean;

/**
 * Opts out of the default prop filtering included when providing
 * `shouldForwardProp` to `styled`.
 *
 * This is rarely needed; typically a simple function or `blockProps` can be
 * used for `shouldForwardProp`.
 *
 * @example
 * ```
 * const Foo = styled('div', {
 *   shouldForwardProp: excludeDefaultFiltering((prop) => prop !== 'bar'),
 * })({
 *   // ...
 * });
 * ```
 *
 * @param shouldForwardProp the predicate to pass directly to emotion
 * @returns a new predicate function which will bypass default filtering
 */
// The generic allows passing a type guard and having that preserved in the returned type.
export const excludeDefaultFiltering = <T extends ShouldForwardProp>(shouldForwardProp: T): T =>
  Object.assign(shouldForwardProp.bind(null) as T, {
    [excludeDefaultFilteringFlag]: true,
  });

const shouldExcludeDefaultFiltering = (shouldForwardProp: ShouldForwardProp) =>
  excludeDefaultFilteringFlag in shouldForwardProp;

const defaultIntrinsicShouldForwardProp: ShouldForwardProp = isPropValid;
const defaultCustomShouldForwardProp: ShouldForwardProp = (prop: string) => prop !== 'theme';

export const addDefaultFiltering = (
  shouldForwardProp: ShouldForwardProp,
  type: StyledComponentType,
): ShouldForwardProp => {
  if (shouldExcludeDefaultFiltering(shouldForwardProp)) {
    return shouldForwardProp;
  }

  const defaultFilter =
    type === 'intrinsic' ? defaultIntrinsicShouldForwardProp : defaultCustomShouldForwardProp;

  return (prop) => defaultFilter(prop) && shouldForwardProp(prop);
};
